import type { Asset, ChecklistAnswer, ChecklistAnswerType, ChecklistContext, ChecklistQuestion, ChecklistTemplate, ChecklistTemplateScope, InspectionContext } from "../types/imobi";

export function isChecklistAnswerNegative(type: ChecklistAnswerType, value: ChecklistAnswer["value"]): boolean {
  if (type === "sim_nao") {
    return value === "nao";
  }

  if (type === "conforme_nao_conforme") {
    return value === "nao_conforme";
  }

  return false;
}

export type ChecklistValidationIssue = {
  questionId: string;
  message: string;
};

export function validateChecklistAnswers(questions: ChecklistQuestion[], answers: ChecklistAnswer[]): ChecklistValidationIssue[] {
  const issues: ChecklistValidationIssue[] = [];
  const answerByQuestionId = new Map(answers.map((answer) => [answer.questionId, answer]));

  questions.forEach((question) => {
    const answer = answerByQuestionId.get(question.id);
    const hasValue = answer?.value !== undefined && answer?.value !== null && answer?.value !== "";

    if (question.required && !hasValue) {
      issues.push({ questionId: question.id, message: `"${question.label}" é obrigatório.` });
      return;
    }

    if (!answer || !hasValue) {
      return;
    }

    if (question.requireDetailOnNegative && isChecklistAnswerNegative(question.type, answer.value)) {
      if (!answer.detailText?.trim()) {
        issues.push({ questionId: question.id, message: `Descreva o problema encontrado em "${question.label}".` });
      }
      if (!answer.photoUrls?.length) {
        issues.push({ questionId: question.id, message: `Anexe uma foto para "${question.label}".` });
      }
    }
  });

  return issues;
}

function templateScopeMatchesAsset(template: ChecklistTemplate, asset: Asset): boolean {
  if (template.scope === "bem") {
    return template.assetId === asset.id;
  }

  if (template.scope === "tipo") {
    return Boolean(template.assetTypeId) && template.assetTypeId === asset.assetTypeId;
  }

  if (template.scope === "grupo") {
    return Boolean(template.assetGroup) && template.assetGroup === asset.assetGroup;
  }

  return template.scope === "geral";
}

const scopeSpecificity: Record<ChecklistTemplateScope, number> = { bem: 0, tipo: 1, grupo: 2, geral: 3 };

/**
 * A retirada/devolução event can have more than one applicable checklist at once
 * (e.g. a general one for every asset plus one specific to this exact item), so this
 * returns every match instead of picking a single "best" template like the old
 * scope-only resolver did. Ordered most-specific first for display.
 */
export function resolveChecklistTemplatesForAsset(templates: ChecklistTemplate[], asset: Asset, context: ChecklistContext): ChecklistTemplate[] {
  return templates
    .filter((template) => template.active && template.context === context && templateScopeMatchesAsset(template, asset))
    .sort((left, right) => scopeSpecificity[left.scope] - scopeSpecificity[right.scope]);
}

/**
 * Checklist templates only distinguish retirada/devolução. Transferência and
 * auditoria inspections reuse the devolução checklist since a handover is
 * conceptually closest to a return by the previous holder.
 */
export function checklistContextForInspection(context: InspectionContext): ChecklistContext {
  return context === "retirada" ? "retirada" : "devolucao";
}
